/**
 * Pipeline trend helpers shared by the Pipeline funnel / trend tiles and the
 * Summary readout tokens (quick-261104-ia3 redesign edits, John 2026-11-04).
 * Pure.
 *
 * Window: exactly 12 month-ends back plus "now", rolling. In Nov 2026 the
 * series runs Nov 25 month-end … Oct 26 month-end, then Now.
 *
 * Potential opps are valued the way the tiles value them: potentialCount ×
 * the going average deal size.
 */
import { money } from './fmt.js';

import type { ExecFunnelPanel, ExecFunnelSnapshot } from '@saledash/shared';

/** Sum of open $ across every stage in a snapshot. */
export function openTotal(s: ExecFunnelSnapshot): number {
  return Object.values(s.byStage).reduce((sum, v) => sum + v, 0);
}

/** Potential $ for a snapshot (0 when no average deal size is known). */
export function potentialValue(s: ExecFunnelSnapshot, avgDeal: number | null): number {
  return avgDeal != null ? s.potentialCount * avgDeal : 0;
}

/** Open + potential $ for a snapshot. */
export function openWithPotential(s: ExecFunnelSnapshot, avgDeal: number | null): number {
  return openTotal(s) + potentialValue(s, avgDeal);
}

/**
 * The trend points: the last 12 month-ends that fall on or after the same month
 * one year ago (and before the current month), then the live point. The live
 * point's potentialCount is the current potential list length.
 */
export function trendPoints(funnel: ExecFunnelPanel, today: string): ExecFunnelSnapshot[] {
  const y = Number(today.slice(0, 4));
  const m = today.slice(5, 7);
  const startKey = `${String(y - 1)}-${m}`;
  const currentKey = today.slice(0, 7);
  const months = funnel.history
    .filter((h) => {
      const k = h.asOf.slice(0, 7);
      return k >= startKey && k < currentKey;
    })
    .slice(-12);
  return [...months, { ...funnel.current, potentialCount: funnel.potential.length }];
}

export interface PipelineChange {
  first: ExecFunnelSnapshot;
  from: number;
  to: number;
  diff: number;
  /** diff ÷ from, null when `from` is 0. */
  ratio: number | null;
}

/** Change in open + potential $ from the first trend point to now. Null with < 2 points. */
export function pipelineChange(points: ExecFunnelSnapshot[], avgDeal: number | null): PipelineChange | null {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || points.length < 2) return null;
  const from = openWithPotential(first, avgDeal);
  const to = openWithPotential(last, avgDeal);
  return { first, from, to, diff: to - from, ratio: from !== 0 ? (to - from) / Math.abs(from) : null };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2025-11-30' → 'Nov 25' */
export function monthEndLabel(iso: string): string {
  return `${MONTHS[Number(iso.slice(5, 7)) - 1] ?? iso} ${iso.slice(2, 4)}`;
}

/**
 * Plain-English change, e.g. "up $2.1M (+31%) since Nov 25",
 * "down $400K (−6%) since Nov 25", "flat since Nov 25".
 */
export function describePipelineChange(c: PipelineChange): string {
  const since = `since ${monthEndLabel(c.first.asOf)}`;
  if (Math.abs(c.diff) < 1 || (c.ratio != null && Math.abs(c.ratio) < 0.005)) return `flat ${since}`;
  const dir = c.diff > 0 ? 'up' : 'down';
  const p = c.ratio != null ? ` (${c.diff > 0 ? '+' : '−'}${String(Math.round(Math.abs(c.ratio) * 100))}%)` : '';
  return `${dir} ${money(Math.abs(c.diff))}${p} ${since}`;
}
